import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Icon,
  V2Badge,
  V2Button,
  V2Card,
  V2CardHeader,
  V2EmptyState,
  LoadingState,
  type SakhtarIconName,
  type V2BadgeTone,
  V2CloseButton,
} from '@sakhtar/design-system/react';
import { tenantBasePath } from '../../lib/tenant.js';
import { useApiGet } from '../../lib/useApiGet.js';
import { apiGet, apiPost, apiPut } from '../../lib/apiClient.js';
import { useAmountDisplay } from '../../lib/tenantSettings.js';
import { formatRialFa } from '../../lib/finance-display.js';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';
import { bannerMessageFrom } from '../../lib/formErrors.js';

interface FlowStep {
  readonly no: string;
  readonly icon: SakhtarIconName;
  readonly title: string;
  readonly description: string;
}

const FLOW_STEPS: readonly FlowStep[] = [
  {
    no: '۱',
    icon: 'invoice',
    title: 'صدور صورتحساب فروش',
    description: 'صورتحساب فروش در ERP صادر و قطعی می‌شود.',
  },
  {
    no: '۲',
    icon: 'upload',
    title: 'ارسال به سامانه',
    description: 'ارسال غیرهمگام با امضای دیجیتال نامتقارن RSA/ECC.',
  },
  {
    no: '۳',
    icon: 'search',
    title: 'استعلام وضعیت',
    description: 'استعلام همگام با شمارهٔ مرجع جهت دریافت نتیجه پردازش.',
  },
  {
    no: '۴',
    icon: 'check',
    title: 'واکنش خریدار',
    description: 'خریدار صورتحساب را در کارپوشهٔ خود تأیید یا رد می‌کند.',
  },
];

const SYSTEM_STATUSES: readonly { tone: V2BadgeTone; label: string }[] = [
  { tone: 'warn', label: 'PENDING · در انتظار پردازش' },
  { tone: 'success', label: 'SUCCESS · تأیید سامانه' },
  { tone: 'danger', label: 'FAILED · ناموفق' },
  { tone: 'neutral', label: 'NOT_FOUND · یافت نشد' },
  { tone: 'neutral', label: 'TIMEOUT · پایان مهلت پاسخ' },
];

const BUYER_REACTIONS: readonly { tone: V2BadgeTone; label: string }[] = [
  { tone: 'warn', label: 'در انتظار واکنش' },
  { tone: 'success', label: 'تأیید شده' },
  { tone: 'danger', label: 'رد شده' },
  { tone: 'neutral', label: 'عدم نیاز به واکنش' },
  { tone: 'neutral', label: 'عدم امکان واکنش' },
];

const REFERENCE_KINDS: readonly { tone: V2BadgeTone; label: string; text: string }[] = [
  {
    tone: 'info',
    label: 'اصلاحی',
    text: 'برای تغییر مبلغ یا روش تسویه. صورتحساب اصلی در کارپوشه «رد شده» می‌شود و اصلاحی ردیف جدید می‌گیرد.',
  },
  {
    tone: 'neutral',
    label: 'ابطالی',
    text: 'لغو کامل صورتحساب. برای تغییر اطلاعات خریدار باید ابطال و صورتحساب جدید صادر شود.',
  },
  {
    tone: 'warn',
    label: 'برگشت از فروش',
    text: 'کاهش تعداد یا مقدار اقلام. اطلاعات خریدار قابل تغییر نیست.',
  },
];

const mutedNote = {
  fontSize: 12,
  lineHeight: 1.75,
  color: 'var(--sk-fg-muted)',
} as const;

function LegendRow({
  title,
  items,
  trailing,
}: {
  readonly title: string;
  readonly items: readonly { tone: V2BadgeTone; label: string }[];
  readonly trailing?: string;
}) {
  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '6px 8px',
        padding: '10px 14px',
        borderTop: '1px dashed var(--sk-divider)',
        fontSize: 11.5,
        color: 'var(--sk-fg-muted)',
      }}
    >
      <span style={{ fontWeight: 600, color: 'var(--sk-fg)' }}>{title}</span>
      {items.map((item) => (
        <V2Badge key={item.label} tone={item.tone}>
          {item.label}
        </V2Badge>
      ))}
      {trailing ? <span style={{ marginInlineStart: 'auto' }}>{trailing}</span> : null}
    </div>
  );
}

interface MoadianSettingsDto {
  readonly settingId?: string;
  readonly taxpayerMemoryId: string;
  readonly sellerEconomicCode: string;
  readonly sellerNationalId: string | null;
  readonly hasPrivateKey: boolean;
  readonly hasPublicKey: boolean;
  readonly hasCertificate: boolean;
  readonly encryptionKeyId: string | null;
  readonly environment: 'sandbox' | 'production';
  readonly isEnabled: boolean;
  readonly transferMethod: 'direct' | 'tsp';
}

interface MoadianInvoiceItemDto {
  readonly moadianInvoiceId: string;
  readonly salesInvoiceId: string | null;
  readonly taxId: string;
  readonly referenceTaxId: string | null;
  readonly invoiceSubject: number;
  readonly invoicePattern: number;
  readonly invoiceType: number;
  readonly issueDate: string;
  readonly sellerEconomicCode: string;
  readonly buyerName: string | null;
  readonly buyerEconomicCode: string | null;
  readonly totalInvoiceRial: string;
  readonly totalVatRial: string;
  readonly status: string;
  readonly referenceNumber: string | null;
  readonly errorCode: string | null;
  readonly errorMessage: string | null;
  readonly buyerStatus: string;
  readonly submittedAt: string | null;
  readonly inquiredAt: string | null;
  readonly createdAt: string;
}

interface MoadianInvoiceListDto {
  readonly items: readonly MoadianInvoiceItemDto[];
  readonly total?: number;
  readonly limit?: number;
  readonly offset?: number;
}

/** `GET finance/moadian/invoices/:id` lines as the detail table reads them. */
interface MoadianInvoiceDetailItem {
  readonly item_id: string;
  readonly row_number: number;
  readonly commodity_code: string;
  readonly commodity_description: string;
  readonly quantity: string | number;
  readonly total_item_rial: string;
}

interface MoadianInvoiceDetail {
  readonly items?: readonly MoadianInvoiceDetailItem[];
}

export function ModianPage() {
  const { slug } = useParams<{ slug: string }>();
  const base = slug ? tenantBasePath(slug) : '';
  const { unit } = useAmountDisplay();

  const [refreshKey, setRefreshKey] = useState(0);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchQuery] = useState<string>('');

  // Modals & form state
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [isTestConnectionLoading, setIsTestConnectionLoading] = useState(false);
  const [testResult, setTestResult] = useState<{
    status: string;
    sampleTaxId: string;
    message: string;
  } | null>(null);

  const [isBatchInquiryLoading, setIsBatchInquiryLoading] = useState(false);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [selectedInvoice, setSelectedInvoice] = useState<MoadianInvoiceItemDto | null>(null);
  const [invoiceDetails, setInvoiceDetails] = useState<MoadianInvoiceDetail | null>(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);

  // Settings form
  const [settingsForm, setSettingsForm] = useState({
    taxpayerMemoryId: '',
    sellerEconomicCode: '',
    sellerNationalId: '',
    privateKey: '',
    environment: 'sandbox' as 'sandbox' | 'production',
    transferMethod: 'direct' as 'direct' | 'tsp',
    isEnabled: true,
  });
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [settingsError, setSettingsError] = useState<string | null>(null);

  // Fetch settings
  const settingsResult = useApiGet<MoadianSettingsDto>(`${base}/api/v1/finance/moadian/settings`, [
    refreshKey,
  ]);

  // Fetch invoice queue with limit=200
  const queryUrl = `${base}/api/v1/finance/moadian/invoices?limit=200&${
    statusFilter !== 'all' ? `status=${statusFilter}&` : ''
  }${searchQuery ? `search=${encodeURIComponent(searchQuery)}` : ''}`;

  const invoicesResult = useApiGet<MoadianInvoiceListDto>(queryUrl, [
    refreshKey,
    statusFilter,
    searchQuery,
  ]);

  const settings = settingsResult.status === 'ready' ? settingsResult.data : null;
  const invoices = invoicesResult.status === 'ready' ? invoicesResult.data.items : [];

  // Open settings modal prefilled
  function handleOpenSettings() {
    if (settings) {
      setSettingsForm({
        taxpayerMemoryId: settings.taxpayerMemoryId || '',
        sellerEconomicCode: settings.sellerEconomicCode || '',
        sellerNationalId: settings.sellerNationalId || '',
        privateKey: '',
        environment: settings.environment || 'sandbox',
        transferMethod: settings.transferMethod || 'direct',
        isEnabled: settings.isEnabled ?? true,
      });
    }
    setSettingsError(null);
    setIsSettingsModalOpen(true);
  }

  async function handleSaveSettings(e: React.FormEvent) {
    e.preventDefault();
    setIsSavingSettings(true);
    setSettingsError(null);
    try {
      await apiPut(`${base}/api/v1/finance/moadian/settings`, {
        taxpayerMemoryId: settingsForm.taxpayerMemoryId.trim().toUpperCase(),
        sellerEconomicCode: settingsForm.sellerEconomicCode.trim(),
        sellerNationalId: settingsForm.sellerNationalId.trim() || undefined,
        privateKey: settingsForm.privateKey.trim() || undefined,
        environment: settingsForm.environment,
        transferMethod: settingsForm.transferMethod,
        isEnabled: settingsForm.isEnabled,
      });
      setIsSettingsModalOpen(false);
      setRefreshKey((k) => k + 1);
      setActionMessage('تنظیمات سامانه مودیان با موفقیت ذخیره شد.');
    } catch (err) {
      setSettingsError(bannerMessageFrom(err));
    } finally {
      setIsSavingSettings(false);
    }
  }

  async function handleTestConnection() {
    setIsTestConnectionLoading(true);
    setTestResult(null);
    setActionMessage(null);
    try {
      const res = await apiPost<{
        status: string;
        sampleTaxId: string;
        message: string;
      }>(`${base}/api/v1/finance/moadian/test-connection`);
      setTestResult(res);
    } catch (err) {
      setTestResult({
        status: 'error',
        sampleTaxId: '—',
        message: bannerMessageFrom(err),
      });
    } finally {
      setIsTestConnectionLoading(false);
    }
  }

  async function handleSubmitInvoice(id: string) {
    try {
      setActionMessage('در حال امضای دیجیتال و ارسال صورتحساب به سامانه مودیان...');
      const res = await apiPost<{ referenceNumber: string; message: string }>(
        `${base}/api/v1/finance/moadian/invoices/${id}/submit`,
      );
      setActionMessage(`صورتحساب با موفقیت ارسال شد (شماره پیگیری: ${res.referenceNumber})`);
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setActionMessage(`خطا در ارسال: ${bannerMessageFrom(err)}`);
    }
  }

  async function handleInquireInvoice(id: string) {
    try {
      setActionMessage('در حال استعلام وضعیت از سامانه امور مالیاتی...');
      const res = await apiPost<{ status: string; message: string }>(
        `${base}/api/v1/finance/moadian/invoices/${id}/inquiry`,
      );
      setActionMessage(`استعلام انجام شد: وضعیت سامانه ${res.status}`);
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setActionMessage(`خطا در استعلام: ${bannerMessageFrom(err)}`);
    }
  }

  async function handleBatchInquiry() {
    setIsBatchInquiryLoading(true);
    try {
      const res = await apiPost<{ processedCount: number; updatedCount: number; message: string }>(
        `${base}/api/v1/finance/moadian/batch-inquiry`,
      );
      setActionMessage(res.message);
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setActionMessage(`خطا در استعلام گروهی: ${bannerMessageFrom(err)}`);
    } finally {
      setIsBatchInquiryLoading(false);
    }
  }

  async function handleViewDetails(inv: MoadianInvoiceItemDto) {
    setSelectedInvoice(inv);
    setIsLoadingDetails(true);
    try {
      const details = await apiGet<MoadianInvoiceDetail>(
        `${base}/api/v1/finance/moadian/invoices/${inv.moadianInvoiceId}`,
      );
      setInvoiceDetails(details);
    } catch {
      setInvoiceDetails(null);
    } finally {
      setIsLoadingDetails(false);
    }
  }

  function getStatusBadge(status: string): { tone: V2BadgeTone; label: string } {
    switch (status) {
      case 'success':
        return { tone: 'success', label: 'SUCCESS · تأیید سامانه' };
      case 'pending_inquiry':
        return { tone: 'warn', label: 'PENDING · در انتظار پردازش' };
      case 'queued':
        return { tone: 'warn', label: 'در صف ارسال' };
      case 'failed':
        return { tone: 'danger', label: 'FAILED · ناموفق' };
      case 'not_found':
        return { tone: 'neutral', label: 'NOT_FOUND · یافت نشد' };
      case 'timeout':
        return { tone: 'neutral', label: 'TIMEOUT · مهلت منقضی' };
      default:
        return { tone: 'neutral', label: 'پیش‌نویس' };
    }
  }

  function getBuyerBadge(status: string): { tone: V2BadgeTone; label: string } {
    switch (status) {
      case 'approved':
        return { tone: 'success', label: 'تأیید شده' };
      case 'rejected':
        return { tone: 'danger', label: 'رد شده' };
      case 'pending':
        return { tone: 'warn', label: 'در انتظار واکنش' };
      case 'not_required':
        return { tone: 'neutral', label: 'عدم نیاز به واکنش' };
      default:
        return { tone: 'neutral', label: '—' };
    }
  }

  function getSubjectLabel(subject: number): string {
    switch (subject) {
      case 1:
        return 'اصلی';
      case 2:
        return 'اصلاحی';
      case 3:
        return 'ابطالی';
      case 4:
        return 'برگشت از فروش';
      default:
        return 'نامشخص';
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Top Banner Message */}
      {actionMessage && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '10px 16px',
            background: 'var(--sk-bg-elev)',
            borderRadius: 8,
            border: '1px solid var(--sk-brand-500)',
            fontSize: 13,
            color: 'var(--sk-fg)',
          }}
        >
          <span>{actionMessage}</span>
          <button
            onClick={() => setActionMessage(null)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--sk-fg-muted)',
            }}
          >
            ✕
          </button>
        </div>
      )}

      {/* Main Status & Quick Test Card */}
      <V2Card>
        <V2CardHeader
          icon="shield"
          title="سامانه مودیان و پایانه‌های فروشگاهی"
          subtitle="ارسال صورتحساب الکترونیکی فروش، تولید شناسه یکتای ۲۲ رقمی و استعلام کارپوشه"
          actions={
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {settings?.isEnabled ? (
                <V2Badge tone="success">
                  {settings.environment === 'sandbox' ? 'محیط آزمایشی (Sandbox)' : 'محیط عملیاتی'}
                </V2Badge>
              ) : (
                <V2Badge tone="warn">تنظیم اولیه لازم است</V2Badge>
              )}
              <V2Button
                variant="secondary"
                size="sm"
                onClick={handleTestConnection}
                disabled={isTestConnectionLoading || !settings?.taxpayerMemoryId}
              >
                {isTestConnectionLoading ? 'در حال آزمون...' : 'آزمون اتصال'}
              </V2Button>
              <V2Button variant="primary" size="sm" onClick={handleOpenSettings}>
                تنظیمات اتصال
              </V2Button>
            </div>
          }
        />

        {testResult && (
          <div
            style={{
              margin: '10px 14px',
              padding: '12px 14px',
              borderRadius: 8,
              background:
                testResult.status === 'ok'
                  ? 'color-mix(in oklch, var(--sk-color-success-base), transparent 90%)'
                  : 'color-mix(in oklch, var(--sk-color-danger-base), transparent 90%)',
              border: `1px solid ${
                testResult.status === 'ok'
                  ? 'var(--sk-color-success-base)'
                  : 'var(--sk-color-danger-base)'
              }`,
              fontSize: 12.5,
              lineHeight: 1.8,
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: 4 }}>{testResult.message}</div>
            {testResult.sampleTaxId !== '—' && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ color: 'var(--sk-fg-muted)' }}>نمونه شناسه مالیاتی تولیدی:</span>
                <code
                  className="sk-num"
                  style={{
                    fontWeight: 700,
                    letterSpacing: 1,
                    direction: 'ltr',
                    background: 'var(--sk-bg)',
                    padding: '2px 8px',
                    borderRadius: 4,
                  }}
                >
                  {testResult.sampleTaxId}
                </code>
              </div>
            )}
          </div>
        )}

        <div style={{ padding: '12px 14px', ...mutedNote, fontSize: 13, lineHeight: 1.9 }}>
          در این بخش، کلیه فاکتورهای فروش تأییدشده ERP با شناسه یکتای مالیاتی ۲۲ کاراکتری بر اساس
          کلید اختصاصی، شناسه حافظه مالیاتی و تاریخ رمزنگاری و به صورت غیرهمگام به سامانه امور
          مالیاتی ارسال و وضعیت واکنش خریدار در کارپوشه استعلام می‌شود.
        </div>
      </V2Card>

      {/* Submission Flow Steps Guide */}
      <V2Card>
        <V2CardHeader
          icon="trend"
          title="جریان ارسال صورتحساب به سامانه مودیان"
          subtitle="ارسال غیرهمگام، استعلام همگام · راهنمای مراحل"
        />
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: 10,
            padding: 14,
          }}
        >
          {FLOW_STEPS.map((step) => (
            <div
              key={step.no}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 7,
                padding: '11px 12px',
                borderRadius: 12,
                background: 'var(--sk-bg-elev)',
                border: '1px solid var(--sk-border)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 6,
                }}
              >
                <span
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 9,
                    display: 'grid',
                    placeContent: 'center',
                    color: 'var(--sk-brand-600)',
                    background: 'color-mix(in oklch, var(--sk-brand-500), transparent 88%)',
                  }}
                >
                  <Icon name={step.icon} size="sm" />
                </span>
                <span
                  className="sk-num"
                  style={{ fontSize: 11, fontWeight: 700, color: 'var(--sk-fg-subtle)' }}
                >
                  مرحلهٔ {step.no}
                </span>
              </div>
              <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--sk-fg)' }}>
                {step.title}
              </span>
              <span style={{ fontSize: 11.5, lineHeight: 1.75, color: 'var(--sk-fg-muted)' }}>
                {step.description}
              </span>
            </div>
          ))}
        </div>
        <LegendRow
          title="وضعیت پاسخ سامانه:"
          items={SYSTEM_STATUSES}
          trailing="استعلام: حداکثر ۱۰۰ صورتحساب در هر درخواست، بازهٔ زمانی حداکثر یک هفته"
        />
      </V2Card>

      {/* Main Two Columns: Queue Table + Settings & Reference Panel */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-start' }}>
        {/* Left Column: Live Submission Queue Table */}
        <div style={{ flex: '2 1 640px', minWidth: 0 }}>
          <V2Card>
            <V2CardHeader
              icon="invoice"
              title="صف ارسال و استعلام صورتحساب‌ها"
              subtitle={
                invoicesResult.status === 'ready'
                  ? `${(invoicesResult.data.total ?? invoices.length).toLocaleString('fa-IR')} صورتحساب الکترونیکی ثبت‌شده`
                  : 'صورتحساب‌های الکترونیکی ثبت‌شده و وضعیت استعلام از سامانه'
              }
              actions={
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    style={{
                      height: 32,
                      padding: '0 8px',
                      borderRadius: 6,
                      border: '1px solid var(--sk-border)',
                      background: 'var(--sk-bg)',
                      color: 'var(--sk-fg)',
                      fontSize: 12,
                    }}
                  >
                    <option value="all">همه وضعیت‌ها</option>
                    <option value="draft">پیش‌نویس</option>
                    <option value="pending_inquiry">در انتظار پردازش</option>
                    <option value="success">تأیید سامانه</option>
                    <option value="failed">ناموفق</option>
                  </select>
                  <V2Button
                    variant="secondary"
                    size="sm"
                    onClick={handleBatchInquiry}
                    disabled={isBatchInquiryLoading || invoices.length === 0}
                  >
                    {isBatchInquiryLoading ? 'در حال استعلام...' : 'استعلام گروهی'}
                  </V2Button>
                </div>
              }
            />

            {/* Invoices Table */}
            {invoicesResult.status === 'loading' ? (
              <div style={{ padding: 40, display: 'flex', justifyContent: 'center' }}>
                <LoadingState label="در حال دریافت صورتحساب‌ها..." />
              </div>
            ) : invoices.length === 0 ? (
              <V2EmptyState
                icon="invoice"
                title="صورتحساب ارسال‌شده‌ای یافت نشد"
                description={
                  settings?.taxpayerMemoryId
                    ? 'جهت ارسال فاکتور به سامانه مودیان، فاکتورهای فروش ثبت‌شده را انتخاب کنید یا فیلتر را تغییر دهید.'
                    : 'ابتدا اطلاعات حافظه مالیاتی و کد اقتصادی را در تب تنظیمات تکمیل کنید.'
                }
              />
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    textAlign: 'right',
                    fontSize: 12.5,
                  }}
                >
                  <thead>
                    <tr
                      style={{
                        background: 'var(--sk-bg-elev)',
                        borderBottom: '1px solid var(--sk-border)',
                      }}
                    >
                      <th style={{ padding: '10px 12px' }}>شناسه یکتای مالیاتی</th>
                      <th style={{ padding: '10px 12px' }}>موضوع</th>
                      <th style={{ padding: '10px 12px' }}>خریدار</th>
                      <th style={{ padding: '10px 12px' }}>مبلغ کل</th>
                      <th style={{ padding: '10px 12px' }}>وضعیت سامانه</th>
                      <th style={{ padding: '10px 12px' }}>واکنش خریدار</th>
                      <th style={{ padding: '10px 12px', textAlign: 'center' }}>عملیات</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((inv) => {
                      const sysBadge = getStatusBadge(inv.status);
                      const bBadge = getBuyerBadge(inv.buyerStatus);
                      return (
                        <tr
                          key={inv.moadianInvoiceId}
                          style={{
                            borderBottom: '1px solid var(--sk-divider)',
                          }}
                        >
                          <td style={{ padding: '10px 12px' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                              <span
                                className="sk-num"
                                style={{
                                  fontWeight: 700,
                                  fontFamily: 'monospace',
                                  direction: 'ltr',
                                  fontSize: 12,
                                  letterSpacing: 0.5,
                                }}
                              >
                                {inv.taxId}
                              </span>
                              <span style={{ fontSize: 11, color: 'var(--sk-fg-muted)' }}>
                                صدور: {displayJalaliDate(inv.issueDate)}
                              </span>
                            </div>
                          </td>
                          <td style={{ padding: '10px 12px' }}>
                            <span style={{ fontWeight: 600 }}>
                              {getSubjectLabel(inv.invoiceSubject)}
                            </span>
                          </td>
                          <td style={{ padding: '10px 12px' }}>
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                              <span>{inv.buyerName || 'خریدار عادی'}</span>
                              <span
                                className="sk-num"
                                style={{ fontSize: 11, color: 'var(--sk-fg-muted)' }}
                              >
                                {inv.buyerEconomicCode || '—'}
                              </span>
                            </div>
                          </td>
                          <td style={{ padding: '10px 12px' }}>
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                              <span className="sk-num" style={{ fontWeight: 700 }}>
                                {formatRialFa(inv.totalInvoiceRial, unit)}
                              </span>
                              <span
                                className="sk-num"
                                style={{ fontSize: 11, color: 'var(--sk-fg-muted)' }}
                              >
                                ارزش افزوده: {formatRialFa(inv.totalVatRial, unit)}
                              </span>
                            </div>
                          </td>
                          <td style={{ padding: '10px 12px' }}>
                            <V2Badge tone={sysBadge.tone}>{sysBadge.label}</V2Badge>
                          </td>
                          <td style={{ padding: '10px 12px' }}>
                            <V2Badge tone={bBadge.tone}>{bBadge.label}</V2Badge>
                          </td>
                          <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                            <div
                              style={{
                                display: 'flex',
                                gap: 6,
                                justifyContent: 'center',
                                alignItems: 'center',
                              }}
                            >
                              {inv.status === 'draft' || inv.status === 'failed' ? (
                                <V2Button
                                  variant="primary"
                                  size="sm"
                                  onClick={() => handleSubmitInvoice(inv.moadianInvoiceId)}
                                >
                                  ارسال
                                </V2Button>
                              ) : (
                                <V2Button
                                  variant="secondary"
                                  size="sm"
                                  onClick={() => handleInquireInvoice(inv.moadianInvoiceId)}
                                >
                                  استعلام
                                </V2Button>
                              )}
                              <V2Button
                                variant="secondary"
                                size="sm"
                                onClick={() => handleViewDetails(inv)}
                              >
                                جزئیات
                              </V2Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <LegendRow
              title="واکنش خریدار در کارپوشه:"
              items={BUYER_REACTIONS}
              trailing="مهلت واکنش خریدار: ۳۰ روز"
            />
          </V2Card>
        </div>

        {/* Right Column: Connection Settings Card & Rules */}
        <div
          style={{
            flex: '1 1 320px',
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          {/* Connection Settings Card */}
          <V2Card>
            <V2CardHeader
              icon="settings"
              title="تنظیمات اتصال API"
              subtitle="اطلاعات کارپوشه و کلیدهای دیجیتال"
              actions={
                <V2Button variant="secondary" size="sm" onClick={handleOpenSettings}>
                  ویرایش
                </V2Button>
              }
            />
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '9px 14px',
                  borderBottom: '1px solid var(--sk-divider)',
                  fontSize: 12.5,
                }}
              >
                <span style={{ color: 'var(--sk-fg-muted)' }}>شناسهٔ یکتای حافظهٔ مالیاتی</span>
                <span className="sk-num" style={{ fontWeight: 700, fontFamily: 'monospace' }}>
                  {settings?.taxpayerMemoryId || '—'}
                </span>
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '9px 14px',
                  borderBottom: '1px solid var(--sk-divider)',
                  fontSize: 12.5,
                }}
              >
                <span style={{ color: 'var(--sk-fg-muted)' }}>کد اقتصادی فروشنده</span>
                <span className="sk-num" style={{ fontWeight: 600 }}>
                  {settings?.sellerEconomicCode || '—'}
                </span>
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '9px 14px',
                  borderBottom: '1px solid var(--sk-divider)',
                  fontSize: 12.5,
                }}
              >
                <span style={{ color: 'var(--sk-fg-muted)' }}>روش ارسال</span>
                <span>
                  {settings?.transferMethod === 'tsp'
                    ? 'شرکت معتمد مالیاتی (TSP)'
                    : 'ارسال مستقیم توسط مودی'}
                </span>
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '9px 14px',
                  borderBottom: '1px solid var(--sk-divider)',
                  fontSize: 12.5,
                }}
              >
                <span style={{ color: 'var(--sk-fg-muted)' }}>امضای دیجیتال (RSA)</span>
                <V2Badge tone={settings?.hasPrivateKey ? 'success' : 'warn'}>
                  {settings?.hasPrivateKey ? 'کلید بارگذاری شده' : 'پیش‌فرض / آزمایشی'}
                </V2Badge>
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '9px 14px',
                  borderBottom: '1px solid var(--sk-divider)',
                  fontSize: 12.5,
                }}
              >
                <span style={{ color: 'var(--sk-fg-muted)' }}>محیط اتصال</span>
                <V2Badge tone={settings?.environment === 'production' ? 'danger' : 'neutral'}>
                  {settings?.environment === 'production' ? 'عملیاتی' : 'آزمایشی (Sandbox)'}
                </V2Badge>
              </div>
            </div>
            <div style={{ padding: '9px 14px', ...mutedNote }}>
              کلید خصوصی و امضای بسته‌ها روی سرور ذخیره و پردازش می‌شود و هرگز به مرورگر ارسال
              نمی‌گردد.
            </div>
          </V2Card>

          {/* Referenced Invoices Rules Card */}
          <V2Card>
            <V2CardHeader
              icon="layers"
              title="صورتحساب‌های ارجاعی"
              subtitle="صورتحساب ارسال‌شده ویرایش نمی‌شود"
            />
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {REFERENCE_KINDS.map((kind) => (
                <div
                  key={kind.label}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3,
                    padding: '9px 14px',
                    borderBottom: '1px solid var(--sk-divider)',
                  }}
                >
                  <span style={{ alignSelf: 'flex-start' }}>
                    <V2Badge tone={kind.tone}>{kind.label}</V2Badge>
                  </span>
                  <span style={mutedNote}>{kind.text}</span>
                </div>
              ))}
              <div style={{ padding: '9px 14px', ...mutedNote }}>
                تاریخ صدور هر صورتحساب ارجاعی باید بعد از تاریخ صورتحساب اصلی باشد.
              </div>
            </div>
          </V2Card>

          <div style={{ fontSize: 12.5 }}>
            <Link to={`${base}/finance/vat`}>گزارش ارزش افزوده</Link>
          </div>
        </div>
      </div>

      {/* Settings Modal */}
      {isSettingsModalOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 16,
          }}
        >
          <div
            style={{
              background: 'var(--sk-bg)',
              borderRadius: 12,
              padding: 24,
              width: '100%',
              maxWidth: 520,
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
              boxShadow: 'var(--sk-shadow-lg)',
            }}
          >
            <div
              style={{ display: 'flex', justifyContent: 'flex-end', margin: '-10px -10px -6px' }}
            >
              <V2CloseButton onClick={() => setIsSettingsModalOpen(false)} />
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: '1px solid var(--sk-border)',
                paddingBottom: 10,
              }}
            >
              <h3 style={{ margin: 0, fontSize: 16 }}>تنظیمات اتصال به سامانه مودیان</h3>
              <button
                onClick={() => setIsSettingsModalOpen(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: 16,
                  color: 'var(--sk-fg-muted)',
                }}
              >
                ✕
              </button>
            </div>

            {settingsError && (
              <div
                style={{
                  padding: 10,
                  borderRadius: 6,
                  background: 'var(--sk-bg-danger)',
                  color: 'var(--sk-fg-danger)',
                  fontSize: 12.5,
                }}
              >
                {settingsError}
              </div>
            )}

            <form
              onSubmit={handleSaveSettings}
              style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
            >
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
                  شناسه یکتای حافظه مالیاتی (۶ کاراکتر اختصاصی از کارپوشه) *
                </label>
                <input
                  type="text"
                  maxLength={6}
                  required
                  placeholder="مثال: A11B2C"
                  value={settingsForm.taxpayerMemoryId}
                  onChange={(e) =>
                    setSettingsForm({ ...settingsForm, taxpayerMemoryId: e.target.value })
                  }
                  style={{
                    width: '100%',
                    height: 36,
                    padding: '0 10px',
                    borderRadius: 6,
                    border: '1px solid var(--sk-border)',
                    background: 'var(--sk-bg-elev)',
                    fontFamily: 'monospace',
                    fontWeight: 700,
                    direction: 'ltr',
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
                  کد اقتصادی فروشنده (حداقل ۱۰ رقم) *
                </label>
                <input
                  type="text"
                  required
                  placeholder="کد اقتصادی ۱۲ یا ۱۴ رقمی سازمان"
                  value={settingsForm.sellerEconomicCode}
                  onChange={(e) =>
                    setSettingsForm({ ...settingsForm, sellerEconomicCode: e.target.value })
                  }
                  style={{
                    width: '100%',
                    height: 36,
                    padding: '0 10px',
                    borderRadius: 6,
                    border: '1px solid var(--sk-border)',
                    background: 'var(--sk-bg-elev)',
                    direction: 'ltr',
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
                  شناسه ملی / کد ملی فروشنده
                </label>
                <input
                  type="text"
                  placeholder="شناسه ملی ۱۱ رقمی شرکت یا کد ملی ۱۰ رقمی"
                  value={settingsForm.sellerNationalId}
                  onChange={(e) =>
                    setSettingsForm({ ...settingsForm, sellerNationalId: e.target.value })
                  }
                  style={{
                    width: '100%',
                    height: 36,
                    padding: '0 10px',
                    borderRadius: 6,
                    border: '1px solid var(--sk-border)',
                    background: 'var(--sk-bg-elev)',
                    direction: 'ltr',
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
                  کلید خصوصی RSA (قالب PEM یا خالی برای تولید خودکار آزمایشی)
                </label>
                <textarea
                  rows={4}
                  placeholder="-----BEGIN PRIVATE KEY----- ... -----END PRIVATE KEY-----"
                  value={settingsForm.privateKey}
                  onChange={(e) => setSettingsForm({ ...settingsForm, privateKey: e.target.value })}
                  style={{
                    width: '100%',
                    padding: 8,
                    borderRadius: 6,
                    border: '1px solid var(--sk-border)',
                    background: 'var(--sk-bg-elev)',
                    fontFamily: 'monospace',
                    fontSize: 11,
                    direction: 'ltr',
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <label
                    style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}
                  >
                    محیط سامانه
                  </label>
                  <select
                    value={settingsForm.environment}
                    onChange={(e) =>
                      setSettingsForm({
                        ...settingsForm,
                        environment: e.target.value as 'sandbox' | 'production',
                      })
                    }
                    style={{
                      width: '100%',
                      height: 36,
                      borderRadius: 6,
                      border: '1px solid var(--sk-border)',
                      background: 'var(--sk-bg-elev)',
                      padding: '0 8px',
                    }}
                  >
                    <option value="sandbox">آزمایشی (Sandbox)</option>
                    <option value="production">عملیاتی (Production)</option>
                  </select>
                </div>

                <div style={{ flex: 1 }}>
                  <label
                    style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}
                  >
                    روش ارسال
                  </label>
                  <select
                    value={settingsForm.transferMethod}
                    onChange={(e) =>
                      setSettingsForm({
                        ...settingsForm,
                        transferMethod: e.target.value as 'direct' | 'tsp',
                      })
                    }
                    style={{
                      width: '100%',
                      height: 36,
                      borderRadius: 6,
                      border: '1px solid var(--sk-border)',
                      background: 'var(--sk-bg-elev)',
                      padding: '0 8px',
                    }}
                  >
                    <option value="direct">مستقیم (توسط مودی)</option>
                    <option value="tsp">شرکت معتمد مالیاتی (TSP)</option>
                  </select>
                </div>
              </div>

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 8,
                  marginTop: 10,
                  borderTop: '1px solid var(--sk-border)',
                  paddingTop: 12,
                }}
              >
                <V2Button
                  variant="secondary"
                  size="md"
                  type="button"
                  onClick={() => setIsSettingsModalOpen(false)}
                >
                  انصراف
                </V2Button>
                <V2Button variant="primary" size="md" type="submit" disabled={isSavingSettings}>
                  {isSavingSettings ? 'در حال ذخیره...' : 'ذخیره تنظیمات'}
                </V2Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Invoice Details Modal */}
      {selectedInvoice && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 16,
          }}
        >
          <div
            style={{
              background: 'var(--sk-bg)',
              borderRadius: 12,
              padding: 24,
              width: '100%',
              maxWidth: 680,
              maxHeight: '90vh',
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
              boxShadow: 'var(--sk-shadow-lg)',
            }}
          >
            <div
              style={{ display: 'flex', justifyContent: 'flex-end', margin: '-10px -10px -6px' }}
            >
              <V2CloseButton onClick={() => setSelectedInvoice(null)} />
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: '1px solid var(--sk-border)',
                paddingBottom: 10,
              }}
            >
              <h3 style={{ margin: 0, fontSize: 16 }}>جزئیات صورتحساب الکترونیکی سامانه مودیان</h3>
              <button
                onClick={() => setSelectedInvoice(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: 16,
                  color: 'var(--sk-fg-muted)',
                }}
              >
                ✕
              </button>
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: 12,
                fontSize: 13,
              }}
            >
              <div>
                <span style={{ color: 'var(--sk-fg-muted)', display: 'block', fontSize: 11 }}>
                  شناسه منحصر به فرد مالیاتی:
                </span>
                <code
                  className="sk-num"
                  style={{
                    fontWeight: 700,
                    letterSpacing: 1,
                    direction: 'ltr',
                    display: 'block',
                    marginTop: 4,
                  }}
                >
                  {selectedInvoice.taxId}
                </code>
              </div>
              <div>
                <span style={{ color: 'var(--sk-fg-muted)', display: 'block', fontSize: 11 }}>
                  شماره پیگیری دریافت سامانه:
                </span>
                <code
                  className="sk-num"
                  style={{
                    fontWeight: 600,
                    direction: 'ltr',
                    display: 'block',
                    marginTop: 4,
                  }}
                >
                  {selectedInvoice.referenceNumber || 'هنوز ارسال نشده'}
                </code>
              </div>
              <div>
                <span style={{ color: 'var(--sk-fg-muted)', display: 'block', fontSize: 11 }}>
                  تاریخ صدور:
                </span>
                <span style={{ fontWeight: 600 }}>
                  {displayJalaliDate(selectedInvoice.issueDate)}
                </span>
              </div>
              <div>
                <span style={{ color: 'var(--sk-fg-muted)', display: 'block', fontSize: 11 }}>
                  خریدار:
                </span>
                <span style={{ fontWeight: 600 }}>
                  {selectedInvoice.buyerName || 'خریدار عادی'}
                </span>
              </div>
              <div>
                <span style={{ color: 'var(--sk-fg-muted)', display: 'block', fontSize: 11 }}>
                  مبلغ کل فاکتور:
                </span>
                <span className="sk-num" style={{ fontWeight: 700 }}>
                  {formatRialFa(selectedInvoice.totalInvoiceRial, unit)}
                </span>
              </div>
              <div>
                <span style={{ color: 'var(--sk-fg-muted)', display: 'block', fontSize: 11 }}>
                  مالیات بر ارزش افزوده:
                </span>
                <span className="sk-num" style={{ fontWeight: 700 }}>
                  {formatRialFa(selectedInvoice.totalVatRial, unit)}
                </span>
              </div>
            </div>

            {isLoadingDetails ? (
              <LoadingState label="در حال دریافت جزئیات اقلام..." />
            ) : invoiceDetails?.items?.length ? (
              <div>
                <h4 style={{ margin: '8px 0', fontSize: 13 }}>اقلام صورتحساب</h4>
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: 12,
                    textAlign: 'right',
                  }}
                >
                  <thead>
                    <tr style={{ background: 'var(--sk-bg-elev)' }}>
                      <th style={{ padding: '6px 8px' }}>ردیف</th>
                      <th style={{ padding: '6px 8px' }}>شناسه کالا/خدمت</th>
                      <th style={{ padding: '6px 8px' }}>شرح</th>
                      <th style={{ padding: '6px 8px' }}>تعداد</th>
                      <th style={{ padding: '6px 8px' }}>مبلغ کل</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoiceDetails.items.map((i) => (
                      <tr key={i.item_id} style={{ borderBottom: '1px solid var(--sk-divider)' }}>
                        <td style={{ padding: '6px 8px' }}>{i.row_number}</td>
                        <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>
                          {i.commodity_code}
                        </td>
                        <td style={{ padding: '6px 8px' }}>{i.commodity_description}</td>
                        <td style={{ padding: '6px 8px' }} className="sk-num">
                          {i.quantity}
                        </td>
                        <td style={{ padding: '6px 8px' }} className="sk-num">
                          {formatRialFa(i.total_item_rial, unit)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}

            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 8,
                borderTop: '1px solid var(--sk-border)',
                paddingTop: 12,
              }}
            >
              <V2Button variant="secondary" size="md" onClick={() => setSelectedInvoice(null)}>
                بستن
              </V2Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
